import React, { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";

const VIDEO_URL = "https://customer-assets.emergentagent.com/job_music-doc-staging/artifacts/0dgz17k3_A011_C013_0516ON_001.mp4";

const HeroSection = () => {
  const videoRef = useRef(null);
  const [isGlitching, setIsGlitching] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleTimeUpdate = () => {
        // Start glitch effect 0.3 seconds before loop
        if (video.currentTime >= 4.2 && video.currentTime < 4.5) {
          setIsGlitching(true);
        }
        // Loop at 4.5 seconds
        if (video.currentTime >= 4.5) {
          video.currentTime = 0;
          setTimeout(() => setIsGlitching(false), 100);
        }
      };
      video.addEventListener("timeupdate", handleTimeUpdate);
      return () => video.removeEventListener("timeupdate", handleTimeUpdate);
    }
  }, []);

  return (
    <section 
      className="hero-video-container cinematic-bars" 
      data-testid="hero-section"
    >
      {/* Video Background */}
      <video
        ref={videoRef}
        className={`hero-video ${isGlitching ? 'video-glitch' : ''}`}
        autoPlay
        muted
        playsInline
        loop
        data-testid="hero-video"
      >
        <source src={VIDEO_URL} type="video/mp4" />
      </video>

      {/* Glitch overlay */}
      {isGlitching && (
        <div className="glitch-overlay" />
      )}

      {/* Overlay */}
      <div className="hero-overlay" />

      {/* Content */}
      <div className="absolute inset-0 z-10 flex flex-col justify-end pb-16 sm:pb-24 px-6 sm:px-12 md:px-24">
        {/* Recording indicator */}
        <motion.div 
          className="flex items-center gap-3 mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
        >
          <div className="w-3 h-3 rounded-full bg-[#E53935] recording-dot" />
          <span className="font-mono text-xs tracking-[0.2em] text-[#888888] uppercase">
            Recording
          </span>
        </motion.div>

        {/* Main Title */}
        <motion.h1 
          className="font-heading text-5xl sm:text-6xl lg:text-[5vw] font-light tracking-tighter leading-none text-[#F5F5F0] mb-2"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 1.2 }}
          data-testid="hero-title"
        >
          5840 FILMS
        </motion.h1>

        {/* Partnership */}
        <motion.div 
          className="font-heading text-xl sm:text-2xl lg:text-[1.8vw] font-light tracking-tight leading-snug text-[#F5F5F0]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 1 }}
          data-testid="hero-tagline"
        >
          <span className="text-[#888888] text-sm sm:text-base lg:text-[1vw]">and</span> AGENTS OF CHANGE
          <br />PRODUCTIONS
        </motion.div>

        {/* Scroll indicator - below text on mobile, centered at bottom on desktop */}
        <motion.div 
          className="flex flex-col items-center gap-2 mt-8 sm:mt-0 sm:absolute sm:bottom-8 sm:left-1/2 sm:-translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
        >
          <span className="font-mono text-xs tracking-[0.2em] text-[#888888] uppercase">
            Scroll
          </span>
          <motion.div 
            className="w-px h-8 bg-[#888888]"
            animate={{ scaleY: [1, 0.5, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
